import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import backgroundVideo from '../artifacts/27239-362518579_small.mp4';
import Button from '../components/Button';
import Link from '../components/Link';
import { useMediaQuery } from 'react-responsive';

const capabilityChips = [
    'Software Engineering',
    'Data Systems',
    'Cloud & DevOps',
    'Workflow Automation',
    'Mission Support'
];

const Home = () => {
    const isSmallWidth = useMediaQuery({ query: '(max-width: 900px)' });
    const isTinyWidth = useMediaQuery({ query: '(max-width: 520px)' });

    return (
        <Box sx={rootSx}>
            <Box component="video" autoPlay loop muted playsInline id="video" sx={videoSx}>
                <source src={backgroundVideo} type="video/mp4" />
            </Box>
            <Box sx={overlaySx} />
            <Box sx={gridSx} />

            <Box sx={topSpacerSx} />
            <Box sx={heroSx}>
                <Typography sx={eyebrowSx}>
                    Gripfire, Inc. · specialized engineering
                </Typography>

                <Typography component="h1" sx={{
                    ...statementSx,
                    ...(isSmallWidth
                        ? { fontSize: 'clamp(2.35rem, 12vw, 5rem)', letterSpacing: '-.055em' }
                        : { fontSize: 'clamp(3.8rem, 6.5vw, 6.8rem)' })
                }}>
                    Durable software for complex systems.
                </Typography>

                <Typography sx={subheadSx}>
                    We help public and private sector teams design, modernize, and deliver
                    mission-focused applications, data systems, and operational workflows — using AI-assisted analysis
                    where it helps teams move faster, reason through complexity, and validate practical options.
                </Typography>

                <Stack direction={isTinyWidth ? 'column' : 'row'} justifyContent="center" alignItems="center" spacing={2} sx={buttonRowSx}>
                    <Link to="/services">
                        <Button variant="contained" size="large">Explore services</Button>
                    </Link>
                    <Link to="/contact">
                        <Button variant="outlined" size="large">Start a conversation</Button>
                    </Link>
                </Stack>

                <Stack direction="row" justifyContent="center" spacing={1.25} useFlexGap flexWrap="wrap" sx={chipRowSx}>
                    {capabilityChips.map((chip) => (
                        <Chip key={chip} label={chip} sx={chipSx} />
                    ))}
                </Stack>
            </Box>
            <Box sx={bottomSpacerSx} />
        </Box>
    );
}

export default Home;

const rootSx = {
    flex: 1,
    minHeight: '100svh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: 0,
    position: 'relative',
    overflowX: 'clip',
    background: '#070707'
};

const videoSx = {
    top: '0',
    left: '0',
    bottom: '0',
    overflow: 'hidden',
    minWidth: '100%',
    minHeight: '100%',
    position: 'fixed',
    opacity: '.35',
    filter: 'saturate(1.25) contrast(1.1)'
};

const overlaySx = {
    position: 'fixed',
    inset: 0,
    background: 'radial-gradient(circle at 50% 38%, rgba(0, 0, 0, .42), transparent 34%), radial-gradient(circle at 82% 22%, rgba(255, 107, 43, .22), transparent 30%), linear-gradient(135deg, rgba(5, 5, 5, .94), rgba(22, 9, 4, .72) 48%, rgba(5, 5, 5, .95))'
};

const gridSx = {
    position: 'fixed',
    inset: 0,
    opacity: .28,
    backgroundImage: 'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)',
    backgroundSize: '72px 72px',
    maskImage: 'radial-gradient(circle at center, black 0%, transparent 72%)'
};

const topSpacerSx = {
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    height: { xs: 'calc(5.75rem + env(safe-area-inset-top))', sm: '6.25rem' }
};

const bottomSpacerSx = {
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    height: { xs: 'calc(16rem + env(safe-area-inset-bottom))', sm: '12rem' },
    '@media (orientation: landscape) and (max-height: 500px)': {
        height: '10rem'
    }
};

const heroSx = {
    mx: 'auto',
    my: 'auto',
    position: 'relative',
    zIndex: 1,
    width: 'min(1120px, calc(100% - 1.5rem))',
    textAlign: 'center',
    py: 0,
    '@media (max-height: 600px)': {
        my: 0
    }
};

const eyebrowSx = {
    mb: 2,
    color: '#ff9b66',
    fontSize: '.78rem',
    fontWeight: 800,
    lineHeight: 1.4,
    letterSpacing: { xs: '.1em', sm: '.18em' },
    textTransform: 'uppercase'
};

const statementSx = {
    color: 'white',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight: 800,
    lineHeight: .95,
    letterSpacing: '-.08em',
    textShadow: '0 0 40px rgba(255, 81, 31, .22)'
};

const subheadSx = {
    mx: 'auto',
    mt: 3,
    maxWidth: '760px',
    color: 'rgba(255,255,255,.78)',
    fontSize: 'clamp(1rem, 2vw, 1.35rem)',
    lineHeight: 1.7
};

const buttonRowSx = {
    pt: '2.5rem',
    '& a': { textDecoration: 'none', width: { xs: '100%', sm: 'auto' } },
    '& button': { width: { xs: '100%', sm: 'auto' }, maxWidth: '320px' }
};

const chipRowSx = {
    mx: 'auto',
    mt: 4,
    mb: '70px',
    maxWidth: '820px',
    '@media (orientation: landscape) and (max-height: 500px)': {
        mb: '100px'
    }
};

const chipSx = {
    color: '#fff',
    border: '1px solid rgba(255, 140, 82, .45)',
    background: 'rgba(255, 255, 255, .06)',
    backdropFilter: 'blur(10px)',
    fontWeight: 600
};
